/**
 * 산출물 버전 다루기.
 *
 * 화면 두 곳이 같은 판단을 한다 — 에이전트 화면은 채널에 올릴 버전을 고르고,
 * 화면 상세는 그 화면에 고정할 버전을 고른다. 정렬이 두 곳에서 달라지면
 * "최신"이 서로 다른 것을 가리키게 되므로 한곳에 둔다.
 */

/** 채널에 올릴 수 있는 버전 하나. 산출물이 아니라 **버전**이 롤아웃의 단위다. */
export interface VersionOption {
  version: string;
  /** 이 버전으로 등록된 플랫폼 이름. 빠진 플랫폼의 화면은 업데이트를 받지 못한다. */
  targets: string[];
  /**
   * 그중 **자동 업데이트까지 되는** 플랫폼.
   *
   * 산출물이 있다고 화면이 스스로 갈아타지는 않는다. 데스크톱은 업데이터 서명이
   * 있어야 설치까지 가고(없으면 받아 놓고 거부한다), 안드로이드는 APK 서명이 그 역할을
   * 하므로 등록만 되어 있으면 된다. 이 차이를 승격 전에 보여주지 않으면 "올렸는데 이
   * 플랫폼만 안 올라간다"를 나중에 현장에서 발견한다.
   */
  autoUpdate: string[];
  /** 산출물 개수(플랫폼 × 아키텍처 × 용도). */
  count: number;
}

/**
 * 산출물 목록을 버전 단위로 접는다.
 *
 * 산출물은 플랫폼·아키텍처마다 하나씩 올라오지만 롤아웃은 버전 하나를 가리킨다.
 * 접지 않고 그대로 고르게 하면 같은 버전이 대여섯 줄로 늘어서고, 그중 무엇을
 * 골라도 결과가 같다는 사실이 화면에 드러나지 않는다.
 */
export function releaseVersions(
  releases: readonly { version: string; target: string; target_label: string; signed: boolean }[],
): VersionOption[] {
  const byVersion = new Map<string, VersionOption>();

  for (const release of releases) {
    const entry =
      byVersion.get(release.version) ??
      { version: release.version, targets: [], autoUpdate: [], count: 0 };

    if (!entry.targets.includes(release.target_label)) entry.targets.push(release.target_label);
    if (canAutoUpdate(release) && !entry.autoUpdate.includes(release.target_label)) {
      entry.autoUpdate.push(release.target_label);
    }
    entry.count += 1;
    byVersion.set(release.version, entry);
  }

  return [...byVersion.values()].sort((a, b) => compareVersions(b.version, a.version));
}

/**
 * 이 산출물로 화면이 스스로 갈아탈 수 있는가.
 *
 * 안드로이드는 APK 서명이 관문이라 등록만 되어 있으면 된다. 데스크톱은 업데이터
 * 서명이 없으면 내려받은 뒤 설치를 거부한다 — 그 거부는 화면 안에만 남으므로
 * 콘솔에서 미리 말해 주지 않으면 아무도 모른다.
 */
function canAutoUpdate(release: { target: string; signed: boolean }): boolean {
  return release.target === 'android' || release.signed;
}

/**
 * semver 비교. `a` 가 크면 양수.
 *
 * 문자열 정렬로 두면 `0.10.0` 이 `0.9.0` 보다 **앞**에 온다. 그러면 목록의 최신 버전이
 * 뒤집히고, "다운그레이드입니다" 경고가 정반대로 뜬다 — 업데이터가 거부해서 되돌릴 수도
 * 없는 조작에 붙는 경고라 뒤집히면 안 된다.
 */
export function compareVersions(a: string, b: string): number {
  const left = a.split('.').map((part) => Number.parseInt(part, 10) || 0);
  const right = b.split('.').map((part) => Number.parseInt(part, 10) || 0);

  for (let i = 0; i < 3; i += 1) {
    const diff = (left[i] ?? 0) - (right[i] ?? 0);
    if (diff !== 0) return diff > 0 ? 1 : -1;
  }
  return 0;
}

/**
 * 파일 이름으로 용도를 짐작한다. **서버와 같은 규칙이다**(`routes/agent.ts`).
 *
 * 업데이터가 받는 것은 압축본(`.tar.gz` · `.zip`)이고 사람이 실행하는 것은 설치
 * 파일이다. 확장자만으로 갈리므로, 올리는 사람이 매번 고르게 두지 않는다 —
 * 매번 고르게 하면 언젠가 틀리고, 틀린 것은 승격한 뒤에야 드러난다.
 */
export function guessKind(filename: string): 'installer' | 'update' {
  const lower = filename.toLowerCase();
  return lower.endsWith('.tar.gz') || lower.endsWith('.tgz') || lower.endsWith('.zip')
    ? 'update'
    : 'installer';
}
